// The signed-in app: home, summary, rewards, policy, claims, settings and help, plus the desktop right column.
import { useEffect, useRef, useState } from 'preact/hooks'
import type { AwardView, ConsentSource, ConsentView, Dashboard, LevelView, Me, PolicyView, Rewards, Summary } from '../../../packages/contract/index.ts'
import { fill, type Lang, type Strings } from './i18n.ts'
import { ApiError, addDays, api, capital, daysUntil, fmtDate, fmtDay, fmtInt, fmtMoney, fmtMonth, fmtPct, fmtWeekday, fmtWhen, reducedMotion, store, toDate } from './lib.ts'
import { mountCustomerPortal } from '../public/sherpa-browser.js'
import { ContactSheet, ErrorState, Icon, Inbox, Otp, Sheet, Skeleton, SocialIcon, Spinner, useApp, useLoad, type OtpState, type Social } from './ui.tsx'
import { passkeysWork, registerPasskey } from './auth.tsx'

export const levelName = (t: Strings, l: LevelView): string => fill(t.levelName, { n: l.number, name: t.levelNames[l.number - 1] ?? '' })
const minus = (pct: number, lang: Lang): string => (pct ? `−${fmtPct(pct, lang)}` : fmtPct(0, lang))
const initials = (me: Me): string => `${me.firstName.charAt(0)}${me.lastName.charAt(0)}`.toUpperCase()

/** Counts from 0 to `to` in one second, unless the device asks for reduced motion. */
function useCountUp(to: number): number {
  const [shown, setShown] = useState(reducedMotion() ? to : 0)
  useEffect(() => {
    if (reducedMotion()) { setShown(to); return }
    let raf = 0
    const t0 = performance.now()
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 1000)
      setShown(Math.round(to * (1 - Math.pow(1 - k, 3))))
      if (k < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [to])
  return shown
}

const BURST = [[-90, -70, 40], [-60, -100, 120], [0, -110, 80], [60, -95, 200], [95, -60, 10], [105, 0, 300], [85, 60, 150], [40, 95, 60], [-30, 100, 250], [-80, 70, 330], [-110, 10, 90], [-100, -30, 180], [20, -80, 270], [70, 30, 20]] as const

function LevelUpSheet({ level }: { readonly level: LevelView }) {
  const app = useApp()
  const { t, lang } = app
  const close = () => app.sheet(null)
  return (
    <Sheet label={t.lvEyebrow} onClose={close}>
      <div class="v-stack-l" style={{ textAlign: 'center', alignItems: 'center', paddingTop: '8px' }}>
        <div class="v-burst" aria-hidden="true">
          {BURST.map(([x, y, r], i) => <span key={i} style={{ '--dx': `${x}px`, '--dy': `${y}px`, '--r': `${r}deg` }} />)}
          <div class="v-medal"><span class="v-num" style={{ fontSize: '52px', fontWeight: 600, color: 'var(--brand-text)', letterSpacing: '-0.04em' }}>{level.number}</span></div>
        </div>
        <div class="v-stack-s"><p class="v-eyebrow">{t.lvEyebrow}</p><h2 class="v-h1" style={{ fontSize: '28px' }}>{fill(t.lvTitle, { lvl: levelName(t, level) })}</h2><p class="v-body">{fill(t.lvBody, { pct: fmtPct(level.discountPercent, lang) })}</p></div>
        <button class="v-btn v-btn--primary" type="button" onClick={close}>{t.cont}</button>
      </div>
    </Sheet>
  )
}

// ───────────── home ─────────────

export function Home() {
  const app = useApp()
  const { t, lang, me } = app
  const load = useLoad(async () => {
    const [rewards, summary] = await Promise.all([api<Rewards>('/api/rewards'), api<Summary>('/api/summary').catch(() => null)])
    app.setDash(rewards.dashboard)
    return { rewards, summary }
  })
  const dash = load.data?.rewards.dashboard ?? null
  const shown = useCountUp(dash?.points ?? 0)
  useEffect(() => {
    if (!dash || !me) return
    const key = `vr-level:${me.policyNumber}`
    const seen = Number(store.get(key) ?? '0')
    if (seen && dash.level.number > seen) app.sheet(<LevelUpSheet level={dash.level} />)
    store.set(key, String(dash.level.number))
  }, [dash?.level.number])
  if (!me) return null
  if (load.loading && !load.data) return <Skeleton />
  if (!load.data || !dash) return <ErrorState error={load.error} onRetry={load.reload} />
  const { rules } = load.data.rewards
  const summary = load.data.summary
  const empty = dash.points === 0 && dash.recent.length === 0
  const ringOff = (314.2 * (1 - Math.min(100, Math.max(0, dash.progressPercent)) / 100)).toFixed(1)
  const left = dash.next && dash.toNext !== null ? fill(t.hmLeft, { n: fmtInt(dash.toNext, lang), lvl: levelName(t, dash.next) }) : t.hmTop
  const labLine = !dash.hasVerifiedLab12Months ? t.hmLabNeed : dash.next?.needsVerifiedLab && dash.lastLab ? fill(t.hmLabOk, { d: fmtDate(dash.lastLab.testDate, lang), lvl: levelName(t, dash.next) }) : ''
  const renewIn = daysUntil(dash.renewalDate)
  const week = summary?.thisWeek ?? null
  return (
    <>
      <div class="v-stack-s" style={{ gap: '2px', paddingTop: '4px' }}><p class="v-small">{fill(t.hmHello, { name: me.firstName })}</p><h1 class="v-h1">{t.hmTitle}</h1></div>
      <div class="v-card v-card--raised v-stack" style={{ padding: '22px' }}>
        <div class="v-between"><span class="v-pill v-pill--brand">{levelName(t, dash.level)}</span><span class="v-pill v-pill--example">{t.example}</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '22px' }}>
          <div class="v-ring" style={{ width: '112px', height: '112px' }} role="img" aria-label={`${levelName(t, dash.level)}, ${fmtPct(dash.progressPercent, lang)}`}>
            <svg viewBox="0 0 120 120" aria-hidden="true"><circle class="trk" cx="60" cy="60" r="50" /><circle class="val" cx="60" cy="60" r="50" style={{ '--c': '314.2', strokeDasharray: '314.2', strokeDashoffset: ringOff }} /></svg>
            <div class="v-ring-c"><span class="v-xs" style={{ fontWeight: 600 }}>{t.level}</span><span class="v-num" style={{ fontSize: '34px', fontWeight: 600, lineHeight: 1, letterSpacing: '-0.03em' }}>{dash.level.number}</span></div>
          </div>
          <div class="v-stack-s" style={{ gap: '2px', minWidth: 0 }}>
            <span class="v-display" aria-hidden="true">{fmtInt(shown, lang)}</span>
            <span class="sr">{fmtInt(dash.points, lang)}</span>
            <span class="v-small">{t.hmPts}</span>
          </div>
        </div>
        {dash.next && (
          <div class="v-stack-s">
            <div class="v-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={dash.progressPercent} aria-label={left}><i style={{ width: `${dash.progressPercent}%` }} /></div>
            <div class="v-between v-xs v-num"><span>{fmtInt(dash.level.minPoints, lang)}</span><span>{fmtInt(dash.next.minPoints, lang)}</span></div>
          </div>
        )}
        <p class="v-body" style={{ color: 'var(--ink)' }}>{left}</p>
        {labLine && <p class="v-small" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}><Icon n="flask" s={16} style={{ marginTop: '3px', color: 'var(--brand-text)' }} /><span>{labLine}</span></p>}
        {empty && (
          <div class="v-stack-s"><p class="v-body">{t.hmEmptyBody}</p><div class="v-grid2">
            <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/connect')} style={{ fontSize: '15px' }}>{t.hmEmptyDex}</button>
            <button class="v-btn v-btn--secondary" type="button" onClick={() => app.nav('/lab')} style={{ fontSize: '15px' }}>{t.hmEmptyLab}</button>
          </div></div>
        )}
        <hr class="v-divider" />
        <div class="v-between" style={{ alignItems: 'flex-end' }}>
          <div class="v-stack-s" style={{ gap: '2px' }}><span class="v-small">{t.hmDisc}</span><span class="v-xs v-num">{fill(t.hmPrem, { a: fmtMoney(dash.premiumNow, dash.currency, lang), b: fmtMoney(dash.premiumAfterRenewal, dash.currency, lang) })}</span></div>
          <span class="v-num" style={{ fontSize: '32px', fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--brand-text)' }}>{minus(dash.level.discountPercent, lang)}</span>
        </div>
      </div>
      {dash.story && (
        <div class="v-card v-card--brand v-stack-s">
          <span class="v-small" style={{ color: 'var(--brand-text)', fontWeight: 600 }}>{fill(t.hmPathT, { lvl: levelName(t, dash.story.reaches) })}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span class="v-num" style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em' }}>{fmtInt(dash.points, lang)} pts</span>
            <Icon n="arrow" style={{ color: 'var(--brand-text)' }} />
            <span class="v-num" style={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em' }}>{fmtInt(dash.points + dash.story.morePoints, lang)} pts</span>
            <span class="v-pill" style={{ background: 'var(--surface)' }}>{levelName(t, dash.story.reaches)}</span>
          </div>
          <p class="v-small" style={{ color: 'var(--ink)' }}>{fill(t.hmPath, { m: dash.nextHba1cDue ? fmtMonth(dash.nextHba1cDue, lang).toLowerCase() : '', p: fmtInt(dash.story.morePoints, lang), lvl: levelName(t, dash.story.reaches), pct: fmtPct(dash.story.reaches.discountPercent, lang), d: fmtDate(dash.renewalDate, lang) })}</p>
        </div>
      )}
      <div class="v-grid2">
        <div class="v-card v-stack-s" style={{ gap: '6px' }}>
          <span class="v-ico v-ico--accent" style={{ width: '36px', height: '36px', borderRadius: '10px' }}><Icon n="flask" /></span>
          <span class="v-xs" style={{ marginTop: '6px' }}>{t.hmNextLab}</span>
          <span class="v-h3">{dash.nextHba1cDue ? fmtMonth(dash.nextHba1cDue, lang) : t.hmNoLab}</span>
          <button class="v-link" type="button" onClick={() => app.nav('/lab')} style={{ minHeight: '36px', alignSelf: 'flex-start' }}>{t.hmUpload}</button>
        </div>
        <div class="v-card v-stack-s" style={{ gap: '6px' }}>
          <span class="v-ico v-ico--brand" style={{ width: '36px', height: '36px', borderRadius: '10px' }}><Icon n="cal" /></span>
          <span class="v-xs" style={{ marginTop: '6px' }}>{t.hmRenew}</span>
          <span class="v-h3">{fmtDate(dash.renewalDate, lang)}</span>
          <span class="v-small" style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>{renewIn > 1 ? fill(t.hmRenewIn, { n: fmtInt(renewIn, lang) }) : renewIn === 1 ? t.hmRenewIn1 : t.hmRenewToday}</span>
        </div>
      </div>
      {week && (
        <>
          <div class="v-card">
            <div class="v-card-h" style={{ marginBottom: '6px' }}><h2 class="v-h3">{t.hmWeek}</h2><span class="v-xs">{fill(t.hmWeekDates, { a: fmtDay(week.weekStart, lang), b: fmtDay(addDays(week.weekStart, 6), lang) })}</span></div>
            <div class="v-list">
              <div class="v-row"><span class="v-ico v-ico--brand"><Icon n="sensor" /></span><div class="v-row-main"><span class="v-row-t">{t.hmWear} · <span class="v-num">{fill(t.hmWearV, { n: week.daysWithData, m: week.daysObserved })}</span></span><span class="v-row-s">{fill(t.hmWearHint, { p: rules.sensorWearWeek })}</span></div></div>
              <div class="v-row"><span class="v-ico v-ico--brand"><Icon n="pulse" /></span><div class="v-row-main"><span class="v-row-t">{t.hmTir}</span><span class="v-row-s">{fill(t.hmTirHint, { p: rules.timeInRangeWeek })}</span></div></div>
            </div>
            <p class="v-xs" style={{ marginTop: '10px' }}>{fill(t.hmWeekPts, { d: fmtWeekday(addDays(week.weekStart, 7), lang) })}</p>
          </div>
          <button class="v-btn v-btn--secondary" type="button" onClick={() => app.nav('/summary')}>{t.hmSeeSummary}</button>
        </>
      )}
    </>
  )
}

// ───────────── summary ─────────────

export function SummaryScreen() {
  const app = useApp()
  const { t, lang, me } = app
  const load = useLoad(() => api<Summary>('/api/summary'))
  if (load.loading && !load.data) return <Skeleton />
  if (!load.data) return <ErrorState error={load.error} onRetry={load.reload} />
  const s = load.data
  const sync = me?.dexcom.lastSyncAt
  const header = <div class="v-stack-s" style={{ gap: '2px', paddingTop: '4px' }}><h1 class="v-h1">{t.smTitle}</h1><p class="v-small">{sync ? fill(t.smSub, { when: fmtWhen(sync, lang) }) : t.smSubNone}</p></div>
  if (!s.dexcomConnected || !s.window) {
    return (
      <>
        {header}
        <div class="v-card v-stack" style={{ alignItems: 'center', textAlign: 'center', padding: '32px 20px' }}>
          <svg viewBox="0 0 200 90" aria-hidden="true" style={{ width: '200px', height: '90px' }}><rect x="10" y="30" width="22" height="50" rx="11" style={{ fill: 'var(--surface-3)' }} /><rect x="42" y="18" width="22" height="62" rx="11" style={{ fill: 'var(--surface-3)' }} /><rect x="74" y="38" width="22" height="42" rx="11" style={{ fill: 'var(--surface-3)' }} /><rect x="106" y="24" width="22" height="56" rx="11" style={{ fill: 'var(--surface-3)' }} /><rect x="138" y="44" width="22" height="36" rx="11" style={{ fill: 'var(--surface-3)' }} /><rect x="170" y="10" width="22" height="70" rx="11" style={{ fill: 'var(--brand-soft)' }} /></svg>
          <div class="v-stack-s"><h2 class="v-h2">{t.smEmptyTitle}</h2><p class="v-body">{t.smEmptyBody}</p></div>
          {!s.dexcomConnected && <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/connect')}>{t.cnDexBtn}</button>}
        </div>
      </>
    )
  }
  const w = s.window
  const tir = w.timeInRangePercent
  return (
    <>
      {header}
      <div class="v-grid2">
        <div class="v-card v-stack-s">
          <span class="v-small v-strong">{t.smWear}</span>
          <span class="v-num" style={{ fontSize: '40px', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}>{fmtPct(w.wearPercent, lang)}</span>
          <span class="v-xs">{t.smWearSub}</span>
          <div class="v-bar" style={{ marginTop: '6px' }} role="img" aria-label={`${t.smWear} ${fmtPct(w.wearPercent, lang)}`}><i style={{ width: `${w.wearPercent}%` }} /></div>
        </div>
        <div class="v-card v-stack-s">
          <span class="v-small v-strong">{t.smTir}</span>
          <span class="v-num" style={{ fontSize: '40px', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}>{tir === null ? '—' : fmtPct(tir, lang)}</span>
          <span class="v-xs">{t.smTirSub}</span>
          <div class="v-bar" style={{ marginTop: '6px' }} role="img" aria-label={`${t.smTir} ${tir === null ? '—' : fmtPct(tir, lang)}. ${t.smGoal}`}><i style={{ width: `${tir ?? 0}%` }} /><span class="v-bar-mark" style={{ left: '70%' }} /></div>
          <span class="v-xs" style={{ textAlign: 'right' }}>{t.smGoal}</span>
        </div>
      </div>
      {w.gmi !== null && <p class="v-xs" style={{ padding: '0 4px' }}>{fill(t.smGmi, { g: fmtPct(w.gmi, lang, 1) })}</p>}
      <div class="v-card">
        <div class="v-card-h"><h2 class="v-h3">{t.smWeeks}</h2><span class="v-xs">{t.smPtsCol}</span></div>
        {s.weeks.length === 0 ? <p class="v-small">{t.smNoWeeks}</p> : (
          <div>
            {s.weeks.map((wk) => (
              <div key={wk.weekStart} class="v-week">
                <span class="v-xs v-num" style={{ color: 'var(--ink-2)', fontWeight: 500 }}>{fill(t.dxRangeV, { a: fmtDay(wk.weekStart, lang), b: fmtDay(addDays(wk.weekStart, 6), lang) })}</span>
                <div class="v-stack-s" style={{ gap: '6px' }}>
                  <div class="v-stack-s" style={{ gap: '3px' }}><div class="v-between v-xs"><span>{t.hmWear}</span><span class="v-num">{fmtPct(wk.wearPercent, lang)}</span></div><div class="v-bar v-bar--thin"><i style={{ width: `${wk.wearPercent}%` }} /></div></div>
                  <div class="v-stack-s" style={{ gap: '3px' }}><div class="v-between v-xs"><span>{t.hmTir}</span><span class="v-num">{wk.timeInRangePercent === null ? '—' : fmtPct(wk.timeInRangePercent, lang)}</span></div><div class="v-bar v-bar--thin"><i style={{ width: `${wk.timeInRangePercent ?? 0}%` }} /><span class="v-bar-mark" style={{ left: '70%', width: '1.5px' }} /></div></div>
                </div>
                <span class="v-num" style={{ textAlign: 'right', fontWeight: 600 }}>+{fmtInt(wk.points, lang)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div class="v-msg"><Icon n="info" /><span>{t.smNote}</span></div>
    </>
  )
}

// ───────────── rewards ─────────────

type RoadItem = { readonly date: string | null; readonly title: string; readonly body: string; readonly cls: string }

export function RewardsScreen() {
  const app = useApp()
  const { t, lang, me } = app
  const load = useLoad(() => api<Rewards>('/api/rewards').then((r) => { app.setDash(r.dashboard); return r }))
  if (load.loading && !load.data) return <Skeleton />
  if (!load.data) return <ErrorState error={load.error} onRetry={load.reload} />
  const { rules, levels, dashboard: d } = load.data
  const rows = [
    { what: t.rwRows.wear, unit: t.rwPerWeek, p: rules.sensorWearWeek },
    { what: t.rwRows.tir, unit: t.rwPerWeek, p: rules.timeInRangeWeek },
    { what: t.rwRows.onTime, unit: fill(t.rwPerYear, { n: rules.labResultsPerYear }), p: rules.labOnTime },
    { what: t.rwRows.lt7, unit: t.rwPerResult, p: rules.band.lt7 },
    { what: t.rwRows['7to8'], unit: t.rwPerResult, p: rules.band['7to8'] },
    { what: t.rwRows['8to9'], unit: t.rwPerResult, p: rules.band['8to9'] },
  ]
  const n = rules.labResultsPerYear
  const noSensorTotal = n * rules.labOnTime + n * rules.band.lt7
  const premiumAt = (l: LevelView) => fmtMoney(Math.round(d.premiumNow * (100 - l.discountPercent)) / 100, d.currency, lang)
  const today = new Date().toISOString().slice(0, 10)
  const items: RoadItem[] = [
    { date: d.programYear.start, title: '', body: t.roadStart, cls: '' },
    ...(me?.dexcom.dataFrom ? [{ date: me.dexcom.dataFrom.slice(0, 10), title: '', body: t.roadSensor, cls: '' }] : []),
    ...(d.lastLab ? [{ date: d.lastLab.testDate, title: '', body: fill(d.lastLab.status === 'verified' ? t.roadLab : t.roadLabReview, { v: fmtPct(d.lastLab.value, lang, 1) }), cls: '' }] : []),
    { date: today, title: t.roadToday, body: fill(t.roadNow, { pts: fmtInt(d.points, lang), lvl: levelName(t, d.level) }), cls: 'is-now' },
    ...(d.nextHba1cDue ? [{ date: d.nextHba1cDue, title: fmtMonth(d.nextHba1cDue, lang), body: d.story ? fill(t.roadNextStory, { p: fmtInt(d.story.morePoints, lang), lvl: levelName(t, d.story.reaches) }) : t.roadNext, cls: 'is-key' }] : []),
    { date: d.renewalDate, title: '', body: t.roadRenew, cls: 'is-key' },
  ]
  const road = items
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
    .map((r) => ({ ...r, title: r.title || (r.date ? fmtDate(r.date, lang) : ''), cls: r.cls || ((r.date ?? '') < today ? 'is-done' : 'is-key') }))
  const split = [
    { l: t.rwSplitL[0] ?? '', v: d.split.sensorWear, c: 'var(--q2)' },
    { l: t.rwSplitL[1] ?? '', v: d.split.timeInRange, c: 'var(--q3)' },
    { l: t.rwSplitL[2] ?? '', v: d.split.lab, c: 'var(--q4)' },
  ]
  const splitTotal = split.reduce((s, x) => s + x.v, 0)
  return (
    <>
      <div class="v-stack-s" style={{ gap: '2px', paddingTop: '4px' }}><h1 class="v-h1">{t.rwTitle}</h1><p class="v-small">{t.rwSub}</p></div>
      <div class="v-card">
        <div class="v-card-h"><h2 class="v-h3">{t.rwEarn}</h2><span class="v-pill v-pill--example">{t.example}</span></div>
        <table class="v-table">
          <thead><tr><th scope="col">{t.rwWhat}</th><th scope="col">{t.points}</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.what}><td style={{ paddingRight: '12px' }}>{r.what}<span class="v-xs" style={{ display: 'block' }}>{r.unit}</span></td><td><span class="v-strong">+{fmtInt(r.p, lang)}</span></td></tr>)}</tbody>
        </table>
        <p class="v-xs" style={{ marginTop: '10px' }}>{fill(t.rwCap, { n: fmtInt(rules.sensorCapPerYear, lang) })}</p>
      </div>
      <div class="v-card">
        <div class="v-card-h"><h2 class="v-h3">{t.rwLevels}</h2><span class="v-pill v-pill--example">{t.example}</span></div>
        <ol class="v-ladder">
          {levels.map((l) => {
            const cur = l.number === d.level.number
            return (
              <li key={l.number} class={`v-lvl${cur ? ' is-current' : ''}${l.number <= d.level.number ? ' is-reached' : ''}`}>
                <span class="v-lvl-n">{l.number}</span>
                <span class="v-stack-s" style={{ gap: '1px', minWidth: 0 }}>
                  <span class="v-h3">{t.levelNames[l.number - 1]}</span>
                  <span class="v-xs v-num">{fill(t.rwFrom, { p: fmtInt(l.minPoints, lang) })} · {fill(t.rwMonth, { m: premiumAt(l) })}</span>
                  {l.needsVerifiedLab && <span class="v-xs" style={{ display: 'flex', gap: '6px', alignItems: 'center', color: 'var(--ink-2)', fontWeight: 600 }}><Icon n="flask" s={16} style={{ color: 'var(--brand-text)' }} />{t.rwReq}</span>}
                  {cur && <span class="v-xs" style={{ color: 'var(--brand-text)', fontWeight: 600 }}>{t.rwYou}</span>}
                </span>
                <span class="v-lvl-pct">{minus(l.discountPercent, lang)}</span>
              </li>
            )
          })}
        </ol>
      </div>
      <div class="v-card v-card--brand v-stack-s">
        <span class="v-h3">{t.rwNoSensor}</span>
        <span class="v-small v-num" style={{ color: 'var(--ink)' }}>{fill(t.rwNoSensorEx, { n, a: fmtInt(rules.labOnTime, lang), b: fmtInt(rules.band.lt7, lang), t: fmtInt(noSensorTotal, lang) })}</span>
      </div>
      <div class="v-card">
        <h2 class="v-h3" style={{ marginBottom: '16px' }}>{t.rwRoad}</h2>
        <ol class="v-tl">
          {road.map((r, i) => <li key={i} class={`v-tl-i ${r.cls}`}><span class="v-tl-dot">{r.cls === 'is-done' && <Icon n="check" s={16} />}</span><span class="v-stack-s" style={{ gap: '1px' }}><span class="v-xs v-num" style={{ fontWeight: 600 }}>{r.title}</span><span class="v-small" style={{ color: 'var(--ink)' }}>{r.body}</span></span></li>)}
        </ol>
      </div>
      {splitTotal > 0 && (
        <div class="v-card v-stack">
          <h2 class="v-h3">{t.rwSplit}</h2>
          <div style={{ display: 'flex', height: '14px', borderRadius: '999px', overflow: 'hidden', gap: '3px' }} role="img" aria-label={split.map((x) => `${x.l} ${x.v}`).join(', ')}>
            {split.filter((x) => x.v > 0).map((x) => <span key={x.l} style={{ flex: x.v, background: x.c }} />)}
          </div>
          <ul class="v-stack-s">{split.map((x) => <li key={x.l} class="v-between v-small"><span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ width: '10px', height: '10px', borderRadius: '3px', background: x.c }} />{x.l}</span><span class="v-num v-strong">{fmtInt(x.v, lang)}</span></li>)}</ul>
        </div>
      )}
      <div class="v-msg v-msg--brand"><Icon n="info" /><span>{t.rwNote}</span></div>
    </>
  )
}

// ───────────── policy and claims ─────────────

/** The Sherpa+ customer portal inside this screen, opened with Sherpa's own helper through /api/portal-session. */
function Portal({ url }: { readonly url: string }) {
  const { t, lang } = useApp()
  const frame = useRef<HTMLIFrameElement>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    const iframe = frame.current
    if (!iframe) return
    setState('loading')
    const target = new URL(url)
    target.searchParams.set('locale', lang)
    const onMessage = (e: MessageEvent<{ type?: string }>) => { if (e.source === iframe.contentWindow && e.data?.type === 'sherpa:resize') setState('ready') }
    window.addEventListener('message', onMessage)
    const timer = window.setTimeout(() => setState((s) => (s === 'loading' ? 'error' : s)), 20_000)
    let dispose: (() => void) | null = null
    try {
      dispose = mountCustomerPortal(iframe, { portalUrl: target.toString(), bootstrapUrl: '/api/portal-session', requestBootstrap: undefined, fetch: undefined, onError: () => setState('error') })
    } catch { setState('error') }
    return () => { dispose?.(); window.removeEventListener('message', onMessage); window.clearTimeout(timer) }
  }, [url, lang, attempt])
  return (
    <div class="v-portal" id="portal">
      {state === 'loading' && <div class="v-stack-s" role="status" style={{ padding: '4px 0 12px' }}><div class="v-skel" style={{ height: '44px' }} /><div class="v-skel" style={{ height: '72px' }} /><p class="v-xs" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}><Spinner />{t.poPortalLoading}</p></div>}
      {state === 'error' && <div class="v-stack-s" role="alert"><div class="v-msg v-msg--accent"><Icon n="info" /><span>{t.poPortalErr}</span></div><button class="v-btn v-btn--secondary v-btn--sm" type="button" style={{ alignSelf: 'flex-start' }} onClick={() => setAttempt((n) => n + 1)}>{t.retry}</button></div>}
      <div style={state === 'ready' ? undefined : { height: 0, overflow: 'hidden' }}>
        <iframe key={attempt} ref={frame} title={t.poPortalTitle} />
      </div>
    </div>
  )
}

export function PolicyScreen() {
  const app = useApp()
  const { t, lang } = app
  const load = useLoad(() => api<PolicyView>('/api/policy'))
  useEffect(() => { if (load.data && location.hash === '#portal') document.getElementById('portal')?.scrollIntoView({ block: 'start' }) }, [load.data])
  if (load.loading && !load.data) return <Skeleton />
  if (!load.data) return <ErrorState error={load.error} onRetry={load.reload} />
  const p = load.data
  const money = (n: number) => fmtMoney(n, p.currency, lang)
  const active = ['active', 'confirmed', 'renewal'].includes(p.status)
  const ended = ['cancelled', 'expired', 'abandoned', 'superseded', 'suspended', 'review_declined'].includes(p.status)
  const portalUrl = p.portalAvailable && p.portalUrl ? p.portalUrl : null
  return (
    <>
      <div class="v-stack-s" style={{ gap: '2px', paddingTop: '4px' }}><h1 class="v-h1">{t.poTitle}</h1><p class="v-small">{t.poFrom}</p></div>
      <div class="v-card">
        <div class="v-card-h"><span class="v-h3">{t.poProduct}</span><span class={`v-pill ${active ? 'v-pill--ok' : ended ? '' : 'v-pill--accent'}`}><span class="v-dot" />{active ? t.poActive : ended ? t.poEnded : t.poPending}</span></div>
        <dl class="v-kv">
          <dt>{t.poNum}</dt><dd>{p.policyNumber}</dd>
          <dt>{t.poCover}</dt><dd>{fmtMoney(p.coverAmount, p.currency, lang, 0)}</dd>
          <dt>{t.poTerm}</dt><dd>{fill(t.poTermV, { n: p.termYears, y: toDate(p.inceptionDate).getFullYear() + p.termYears })}</dd>
          <dt>{t.poPrem}</dt><dd>{fill(t.poMonth, { m: money(p.premiumMonthly) })}</dd>
          <dt>{t.poNext}</dt><dd>{fmtDate(p.nextPaymentDate, lang)}</dd>
        </dl>
      </div>
      <div class="v-card v-card--brand v-stack-s">
        <span class="v-small" style={{ color: 'var(--brand-text)', fontWeight: 600 }}>{t.poAfter}</span>
        <span class="v-num" style={{ fontSize: '36px', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}>{fill(t.poMonth, { m: money(p.premiumAfterRenewal) })}</span>
        <span class="v-small">{fill(t.poAfterSub, { lvl: levelName(t, p.level), pct: fmtPct(p.level.discountPercent, lang) })}</span>
        {p.next && p.premiumAtNextLevel !== null && <span class="v-xs" style={{ color: 'var(--ink-2)' }}>{fill(t.poIfNext, { lvl: levelName(t, p.next), prem: money(p.premiumAtNextLevel) })}</span>}
      </div>
      <div class="v-card v-stack">
        <div class="v-between" style={{ flexWrap: 'wrap' }}><h2 class="v-h3">{t.poManage}</h2><span class="v-pill"><Icon n="lock" s={16} />{t.poPortal}</span></div>
        {portalUrl ? <Portal url={portalUrl} /> : <div class="v-msg"><Icon n="info" /><span>{t.poNoPortal}</span></div>}
        <hr class="v-divider" />
        <div class="v-stack-s"><h3 class="v-h3">{t.poClaimTitle}</h3><p class="v-small">{t.poClaimBody}</p></div>
        <div class="v-stack-s">
          <button class="v-btn v-btn--secondary" type="button" onClick={() => app.nav('/claim')}>{t.poClaimBtn}</button>
          <button class="v-btn v-btn--quiet" type="button" onClick={() => app.sheet(<ContactSheet />)}><Icon n="chat" />{t.ctBtn}</button>
        </div>
      </div>
    </>
  )
}

export function Claim() {
  const app = useApp()
  const { t } = app
  return (
    <div class="v-stack-l" style={{ paddingTop: '8px' }}>
      <span class="v-ico v-ico--brand v-ico--lg"><Icon n="policy" /></span>
      <div class="v-stack-s"><h1 class="v-h1">{t.clTitle}</h1><p class="v-lead">{t.clBody}</p></div>
      <ol class="v-card v-list">
        {t.clSteps.map((s) => <li key={s} class="v-row"><span class="v-ico v-ico--brand" style={{ width: '32px', height: '32px', borderRadius: '10px' }}><Icon n="check" s={16} /></span><span class="v-small" style={{ color: 'var(--ink)' }}>{s}</span></li>)}
      </ol>
      <button class="v-btn v-btn--primary" type="button" onClick={() => app.nav('/policy#portal')}>{t.clBtn}<Icon n="ext" /></button>
    </div>
  )
}

// ───────────── settings and its sheets ─────────────

function WithdrawSheet({ source }: { readonly source: ConsentSource }) {
  const app = useApp()
  const { t } = app
  const [busy, setBusy] = useState(false)
  const close = () => app.sheet(null)
  const confirm = async () => {
    setBusy(true)
    try {
      app.setMe(await api<Me>(`/api/consents/${source}/withdraw`, { method: 'POST', json: {} }))
      close()
      app.toast(t.wdDone)
    } catch { app.toast(t.errGeneric); setBusy(false) }
  }
  return (
    <Sheet label={t.wdTitle} onClose={close}>
      <div class="v-stack-l">
        <div class="v-stack-s"><p class="v-eyebrow">{t.consents[source].short}</p><h2 class="v-h1" style={{ fontSize: '26px' }}>{t.wdTitle}</h2></div>
        <div class="v-stack-s"><h3 class="v-h3">{t.wdHappens}</h3>
          <ul class="v-list">{t.wdLists[source].map((w) => <li key={w} class="v-row" style={{ minHeight: 0, padding: '10px 0' }}><span class="v-ico" style={{ width: '30px', height: '30px', borderRadius: '9px' }}><Icon n="minus" s={16} /></span><span class="v-small" style={{ color: 'var(--ink)' }}>{w}</span></li>)}</ul>
        </div>
        <div class="v-stack-s">
          <button class="v-btn v-btn--primary" type="button" disabled={busy} onClick={() => void confirm()}>{busy ? <Spinner /> : null}{t.wdConfirm}</button>
          <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.wdKeep}</button>
        </div>
      </div>
    </Sheet>
  )
}

function DeleteSheet() {
  const app = useApp()
  const { t } = app
  const [tick, setTick] = useState(false)
  const [busy, setBusy] = useState(false)
  const close = () => app.sheet(null)
  const confirm = async () => {
    if (!tick) return
    setBusy(true)
    try {
      await api<null>('/api/account', { method: 'DELETE', json: { confirm: true } })
      close()
      app.setMe(null)
      app.nav('/deleted', { replace: true })
    } catch { app.toast(t.errGeneric); setBusy(false) }
  }
  return (
    <Sheet label={t.dlTitle} onClose={close}>
      <div class="v-stack-l">
        <h2 class="v-h1" style={{ fontSize: '26px' }}>{t.dlTitle}</h2>
        <div class="v-stack-s"><h3 class="v-h3">{t.dlWeDelete}</h3><ul class="v-stack-s">{t.dlList.map((d) => <li key={d} class="v-small" style={{ display: 'flex', gap: '10px', color: 'var(--ink)' }}><Icon n="bin" s={16} style={{ marginTop: '3px' }} />{d}</li>)}</ul></div>
        <div class="v-card v-card--flat v-stack-s"><h3 class="v-h3">{t.dlKeep}</h3><ul class="v-stack-s">{t.dlKeepList.map((d) => <li key={d} class="v-small" style={{ display: 'flex', gap: '10px' }}><Icon n="lock" s={16} style={{ marginTop: '3px' }} />{d}</li>)}</ul></div>
        <div class="v-msg v-msg--accent"><Icon n="info" /><span>{t.dlEffect}</span></div>
        <label class="v-check"><input type="checkbox" checked={tick} onChange={(e) => setTick(e.currentTarget.checked)} /><span>{t.dlTick}</span></label>
        <div class="v-stack-s">
          <button class="v-btn v-btn--primary" type="button" disabled={!tick || busy} onClick={() => void confirm()}>{busy ? <Spinner /> : null}{t.dlConfirm}</button>
          <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.cancel}</button>
        </div>
      </div>
    </Sheet>
  )
}

function EmailSheet() {
  const app = useApp()
  const { t, me } = app
  const [step, setStep] = useState<0 | 1>(0)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [state, setState] = useState<OtpState>('idle')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const close = () => app.sheet(null)
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const send = async (e: Event) => {
    e.preventDefault()
    if (!valid) { setError(t.emInvalid); return }
    setBusy(true)
    setError('')
    try { await api<null>('/api/email/change', { method: 'POST', json: { email: email.trim() } }); setStep(1) } catch { setError(t.errGeneric) } finally { setBusy(false) }
  }
  const verify = async (value: string) => {
    setState('checking')
    setError('')
    try {
      const next = await api<Me>('/api/email/verify', { method: 'POST', json: { code: value } })
      setState('ok')
      app.setMe(next)
      window.setTimeout(() => { close(); app.toast(t.emDone) }, 700)
    } catch (err) {
      setState('wrong')
      window.setTimeout(() => setCode(''), 400)
      if (err instanceof ApiError && err.code === 'wrongCode') { const n = Number(err.body['triesLeft'] ?? 0); setError(n === 1 ? t.cdWrong1 : fill(t.cdWrong, { n })) }
      else if (err instanceof ApiError && err.code === 'codeExpired') { setError(t.errs.codeExpired.title); setStep(0) }
      else if (err instanceof ApiError && err.code === 'locked') setError(t.errs.locked.body)
      else setError(t.errGeneric)
    }
  }
  const onCode = (v: string) => { setCode(v); if (state === 'wrong') setState('idle'); if (v.length === 6) void verify(v) }
  return (
    <Sheet label={t.emTitle} onClose={close}>
      <div class="v-stack-l">
        <div class="v-stack-s"><h2 class="v-h1" style={{ fontSize: '26px' }}>{t.emTitle}</h2><p class="v-body">{t.emBody}</p></div>
        {step === 0 ? (
          <form class="v-stack" onSubmit={(e) => void send(e)} noValidate>
            <label class="v-field"><span class="v-label">{t.emNew}</span><input class="v-input" type="email" value={email} onInput={(e) => { setEmail(e.currentTarget.value); setError('') }} autocomplete="email" aria-invalid={error ? 'true' : 'false'} />{error && <span class="v-err">{error}</span>}</label>
            <button class="v-btn v-btn--primary" type="submit" disabled={busy}>{busy ? <Spinner /> : null}{t.lpSend}</button>
          </form>
        ) : (
          <div class="v-stack">
            <div class="v-msg v-msg--brand"><Icon n="mail" /><span>{fill(t.emSent, { e: email.trim(), old: me?.email ?? '' })}</span></div>
            <Otp value={code} onChange={onCode} state={state} label={t.cdLabel} />
            <div aria-live="polite">{error && <div class="v-msg v-msg--accent"><Icon n="info" /><span>{error}</span></div>}{state === 'ok' && <div class="v-msg v-msg--ok"><Icon n="check" /><span>{t.emDone}</span></div>}</div>
            <Inbox path="/api/inbox" onUse={onCode} />
          </div>
        )}
        <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.cancel}</button>
      </div>
    </Sheet>
  )
}

function AddMethodSheet() {
  const app = useApp()
  const { t } = app
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const close = () => app.sheet(null)
  const passkey = async () => {
    setBusy(true)
    setNote('')
    try {
      const me = await registerPasskey()
      if (me) { app.setMe(me); close(); app.toast(t.amDone) }
    } catch { setNote(t.pkErr) } finally { setBusy(false) }
  }
  const socials: readonly Social[] = ['apple', 'google', 'facebook', 'microsoft']
  const names = { apple: 'Apple', google: 'Google', facebook: 'Facebook', microsoft: 'Microsoft' }
  return (
    <Sheet label={t.amTitle} onClose={close}>
      <div class="v-stack-l">
        <h2 class="v-h1" style={{ fontSize: '26px' }}>{t.amTitle}</h2>
        <div class="v-stack-s">
          {passkeysWork() && <button class="v-choice" type="button" disabled={busy} onClick={() => void passkey()} style={{ width: '100%', textAlign: 'left' }}><span class="v-ico v-ico--brand"><Icon n="passkey" /></span><span class="v-choice-main">{t.amPasskey}</span>{busy ? <Spinner /> : <Icon n="chev" />}</button>}
          {socials.map((k) => <button key={k} class="v-choice" type="button" onClick={() => setNote(fill(t.amSoc, { m: names[k] }))} style={{ width: '100%', textAlign: 'left' }}><span class="v-ico"><SocialIcon k={k} onDark /></span><span class="v-choice-main">{names[k]}</span><span class="v-pill v-pill--accent">{t.cnSoon}</span></button>)}
        </div>
        <div aria-live="polite">{note && <div class="v-msg"><Icon n="info" /><span>{note}</span></div>}</div>
        <button class="v-btn v-btn--quiet" type="button" onClick={close}>{t.cancel}</button>
      </div>
    </Sheet>
  )
}

function ConsentCard({ c }: { readonly c: ConsentView }) {
  const app = useApp()
  const { t, lang } = app
  const dates = c.status === 'given' && c.givenAt
    ? (c.endsAt ? fill(t.stGiven, { d: fmtDate(c.givenAt, lang), e: fmtDate(c.endsAt, lang) }) : fill(t.stGivenNoEnd, { d: fmtDate(c.givenAt, lang) }))
    : c.status === 'withdrawn' && c.withdrawnAt ? fill(t.stWithdrawnOn, { d: fmtDate(c.withdrawnAt, lang) }) : t.stNeverGiven
  const give = () => { app.afterConsent.current = '/settings'; app.nav(`/consent/${c.source}`) }
  return (
    <div class="v-card v-stack-s">
      <div class="v-between"><span class="v-h3">{t.consents[c.source].short}</span><span class={`v-pill ${c.status === 'given' ? 'v-pill--ok' : ''}`}><span class="v-dot" />{c.status === 'given' ? t.stOn : c.status === 'withdrawn' ? t.stOff : t.stNotGiven}</span></div>
      <span class="v-xs v-num">{dates}</span>
      <span class="v-xs">{fill(t.stFor, { i: c.insurer })}</span>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
        {c.status === 'given'
          ? <button class="v-btn v-btn--secondary v-btn--sm" type="button" onClick={() => app.sheet(<WithdrawSheet source={c.source} />)}><Icon n="undo" s={16} />{t.stWithdraw}</button>
          : <button class="v-btn v-btn--ghost v-btn--sm" type="button" onClick={give}>{c.status === 'withdrawn' ? t.stRegrant : t.stGive}</button>}
      </div>
    </div>
  )
}

export function Settings() {
  const app = useApp()
  const { t, lang, me } = app
  if (!me) return null
  const refresh = () => void api<Me>('/api/me').then(app.setMe, () => app.toast(t.errGeneric))
  const removeMethod = async (id: string, label: string) => {
    try { app.setMe(await api<Me>(`/api/methods/${encodeURIComponent(id)}`, { method: 'DELETE' })); app.toast(fill(t.removed, { m: label })) } catch (e) {
      app.toast(e instanceof ApiError && e.code === 'lastMethod' ? t.lastMethod : t.errGeneric)
    }
  }
  const signOutDevice = async (id: string, name: string) => {
    try { await api<null>(`/api/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }); app.toast(fill(t.signedOutOn, { d: name })); refresh() } catch { app.toast(t.errGeneric) }
  }
  const order: readonly ConsentSource[] = ['program', 'dexcom', 'lab']
  const consents = [...me.consents].sort((a, b) => order.indexOf(a.source) - order.indexOf(b.source))
  return (
    <>
      <h1 class="v-h1" style={{ paddingTop: '4px' }}>{t.stTitle}</h1>
      <div class="v-card v-stack">
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', width: '100%' }}>
          <span class="v-avatar v-avatar--lg" aria-hidden="true">{initials(me)}</span>
          <span class="v-row-main"><span class="v-row-t">{me.firstName} {me.lastName}</span><span class="v-row-s">{me.email}</span><span class="v-row-s v-num">{me.policyNumber}</span></span>
        </div>
        <button class="v-btn v-btn--secondary v-btn--sm" type="button" onClick={() => app.sheet(<EmailSheet />)} style={{ alignSelf: 'flex-start' }}>{t.stChangeEmail}</button>
      </div>

      <div class="v-stack">
        <div class="v-stack-s" style={{ gap: '2px' }}><h2 class="v-h2">{t.stConsents}</h2><p class="v-small">{t.stConsentsSub}</p></div>
        {consents.map((c) => <ConsentCard key={c.source} c={c} />)}
        <a class="v-btn v-btn--secondary" href="/api/consents/record" download><Icon n="download" />{t.stRecord}</a>
      </div>

      <div class="v-stack">
        <h2 class="v-h2">{t.stSecurity}</h2>
        <div class="v-card">
          <div class="v-stack-s" style={{ gap: '2px', marginBottom: '8px' }}><h3 class="v-h3">{t.stMethods}</h3><p class="v-xs">{t.stMethodsSub}</p></div>
          <ul class="v-list">
            {me.methods.map((m) => (
              <li key={m.id} class="v-row">
                <span class="v-ico"><Icon n={m.kind === 'email' ? 'mail' : m.kind === 'sms' ? 'phone' : 'passkey'} /></span>
                <span class="v-row-main"><span class="v-row-t">{t.mKinds[m.kind]}</span><span class="v-row-s v-num">{m.label} · {fill(t.stAdded, { d: fmtDate(m.createdAt, lang) })}</span></span>
                <button class="v-btn v-btn--quiet v-btn--sm" type="button" disabled={me.methods.length <= 1} onClick={() => void removeMethod(m.id, t.mKinds[m.kind])} aria-label={`${t.stRemove} ${t.mKinds[m.kind]}`}>{t.stRemove}</button>
              </li>
            ))}
          </ul>
          <button class="v-btn v-btn--ghost" type="button" onClick={() => app.sheet(<AddMethodSheet />)} style={{ marginTop: '8px' }}><Icon n="plus" />{t.stAddMethod}</button>
        </div>
        <div class="v-card">
          <h3 class="v-h3" style={{ marginBottom: '8px' }}>{t.stDevices}</h3>
          <ul class="v-list">
            {me.devices.map((d) => (
              <li key={d.id} class="v-row">
                <span class="v-ico"><Icon n={/iphone|android|ipad|phone|móvil/i.test(d.device) ? 'phone' : 'laptop'} /></span>
                <span class="v-row-main"><span class="v-row-t">{d.device}</span><span class="v-row-s">{fill(t.stLastSeen, { w: fmtWhen(d.lastSeenAt, lang) })}</span></span>
                {d.current ? <span class="v-pill v-pill--brand">{t.stThis}</span> : <button class="v-btn v-btn--secondary v-btn--sm" type="button" onClick={() => void signOutDevice(d.id, d.device)}>{t.stSignOut}</button>}
              </li>
            ))}
          </ul>
          <button class="v-btn v-btn--secondary" type="button" onClick={() => void app.signOut('all')} style={{ marginTop: '8px' }}>{t.stSignOutAll}</button>
        </div>
        <div class="v-msg"><Icon n="clock" /><span>{fill(t.stAuto, { n: Math.round(me.idleSeconds / 60) })}</span></div>
      </div>

      <div class="v-stack">
        <h2 class="v-h2">{t.stPrefs}</h2>
        <div class="v-card v-stack">
          <div class="v-stack-s"><span class="v-label">{t.langLabel}</span><div class="v-seg v-seg--wide" role="group" aria-label={t.langLabel}><button type="button" aria-pressed={lang === 'es'} onClick={() => app.setLang('es')}>Español</button><button type="button" aria-pressed={lang === 'en'} onClick={() => app.setLang('en')}>English</button></div></div>
          <div class="v-stack-s"><span class="v-label">{t.stTheme}</span><div class="v-seg v-seg--wide" role="group" aria-label={t.stTheme}><button type="button" aria-pressed={app.theme === 'light'} onClick={() => app.setTheme('light')}>{t.stLight}</button><button type="button" aria-pressed={app.theme === 'dark'} onClick={() => app.setTheme('dark')}>{t.stDark}</button></div></div>
        </div>
      </div>

      <div class="v-stack">
        <h2 class="v-h2">{t.stData}</h2>
        <div class="v-card v-stack">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', width: '100%' }}>
            <span class="v-ico"><Icon n="trash" /></span>
            <span class="v-row-main"><span class="v-row-t">{t.stDelete}</span><span class="v-row-s">{t.stDeleteSub}</span></span>
          </div>
          <button class="v-btn v-btn--secondary v-btn--sm" type="button" onClick={() => app.sheet(<DeleteSheet />)} style={{ alignSelf: 'flex-start' }}>{t.stDelete}</button>
        </div>
      </div>
      <div class="v-stack">
        <h2 class="v-h2">{t.stHelp}</h2>
        <button class="v-card" type="button" onClick={() => app.nav('/help')} style={{ display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left', width: '100%', cursor: 'pointer', color: 'var(--ink)' }}>
          <span class="v-ico"><Icon n="help" /></span>
          <span class="v-row-main"><span class="v-row-t">{t.stHelpT}</span><span class="v-row-s">{t.stHelpSub}</span></span>
          <Icon n="chev" style={{ color: 'var(--ink-3)' }} />
        </button>
      </div>
      <button class="v-btn v-btn--quiet" type="button" onClick={() => void app.signOut('manual')}><Icon n="logout" />{t.stSignOut}</button>
    </>
  )
}

export function Help() {
  const app = useApp()
  const { t } = app
  const [open, setOpen] = useState(0)
  return (
    <>
      <div class="v-stack-s" style={{ gap: '2px', paddingTop: '4px' }}><h1 class="v-h1">{t.hpTitle}</h1><p class="v-small">{t.hpSub}</p></div>
      <div class="v-card" style={{ padding: '4px 18px' }}>
        {t.helpFaq.map((f, i) => (
          <div key={f.q} style={{ borderBottom: i < t.helpFaq.length - 1 ? '1px solid var(--line)' : 0 }}>
            <h2 style={{ margin: 0 }}>
              <button type="button" aria-expanded={open === i} aria-controls={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', width: '100%', minHeight: '60px', padding: '14px 0', border: 0, background: 'none', textAlign: 'left', fontSize: '16px', fontWeight: 600, color: 'var(--ink)' }}>
                <span>{f.q}</span><Icon n="plus" style={{ transform: `rotate(${open === i ? 45 : 0}deg)`, transition: 'transform .2s', color: 'var(--ink-3)' }} />
              </button>
            </h2>
            {open === i && <p id={`faq-${i}`} class="v-body v-pop" style={{ paddingBottom: '16px' }}>{f.a}</p>}
          </div>
        ))}
      </div>
      <button class="v-btn v-btn--secondary" type="button" onClick={() => app.sheet(<ContactSheet />)}><Icon n="chat" />{t.ctBtn}</button>
    </>
  )
}

// ───────────── desktop: right column ─────────────

const awardLabel = (t: Strings, a: AwardView): string => t.awards[a.kind]

export function Aside({ dash }: { readonly dash: Dashboard | null }) {
  const app = useApp()
  const { t, lang } = app
  return (
    <aside class="v-aside" aria-label={t.asLabel}>
      <div class="v-card v-stack-s">
        <span class="v-ico v-ico--accent" style={{ width: '36px', height: '36px', borderRadius: '10px' }}><Icon n="flask" /></span>
        <span class="v-xs" style={{ marginTop: '4px' }}>{t.hmNextLab}</span>
        {dash ? <span class="v-h3">{dash.nextHba1cDue ? fmtMonth(dash.nextHba1cDue, lang) : t.hmNoLab}</span> : <span class="v-skel" style={{ height: '20px', width: '60%' }} />}
        {dash && <span class="v-small">{fill(t.asNextB, { d: fmtDate(dash.renewalDate, lang) })}</span>}
        <button class="v-link" type="button" onClick={() => app.nav('/lab')} style={{ alignSelf: 'flex-start', minHeight: '36px' }}>{t.hmUpload}</button>
      </div>
      <div class="v-card">
        <h2 class="v-h3" style={{ marginBottom: '6px' }}>{t.asRecent}</h2>
        {!dash ? <div class="v-stack-s"><span class="v-skel" style={{ height: '14px' }} /><span class="v-skel" style={{ height: '14px', width: '70%' }} /></div>
          : dash.recent.length === 0 ? <p class="v-small">{t.asRecentNone}</p>
          : <ul class="v-list">{dash.recent.map((a, i) => <li key={i} class="v-row" style={{ minHeight: 0, padding: '10px 0' }}><span class="v-row-main"><span class="v-row-t" style={{ fontSize: '14px' }}>{awardLabel(t, a)}</span><span class="v-row-s v-num">{capital(fmtDate(a.date, lang))}</span></span><span class="v-num v-strong" style={{ color: 'var(--brand-text)' }}>+{fmtInt(a.points, lang)}</span></li>)}</ul>}
      </div>
      <div class="v-card v-stack-s">
        <h2 class="v-h3">{t.asIns}</h2>
        <ul class="v-stack-s" style={{ marginTop: '4px' }}>
          <li class="v-small" style={{ display: 'flex', gap: '10px', color: 'var(--ink)' }}><Icon n="check" s={16} style={{ color: 'var(--ok)', marginTop: '3px' }} />{t.asY1}</li>
          <li class="v-small" style={{ display: 'flex', gap: '10px', color: 'var(--ink)' }}><Icon n="check" s={16} style={{ color: 'var(--ok)', marginTop: '3px' }} />{t.asY2}</li>
          <li class="v-small" style={{ display: 'flex', gap: '10px' }}><Icon n="x" s={16} style={{ marginTop: '3px' }} />{t.asN1}</li>
          <li class="v-small" style={{ display: 'flex', gap: '10px' }}><Icon n="x" s={16} style={{ marginTop: '3px' }} />{t.asN2}</li>
        </ul>
      </div>
    </aside>
  )
}

export const userInitials = initials
